<template>
  <div class="bg-tiara dark:bg-blueDianne-1000">
    <div v-if="notFound" class="container h-full w-full min-w-full">
      <div
        class="w-full pt-[200px] pb-[150px] max-w-[1200px] px-4 md:px-8 mx-auto relative z-3"
      >
        <h3
          class="text-3xl text-center font-headline text-blueDianne dark:text-white"
        >
          Sorry, we could not find this expert area.
        </h3>
        <h4 class="text-center mt-10">
          <a
            class="bg-blueDianne text-white rounded-l-full rounded-b-full px-12 py-3 font-headline text-xl font-bold"
            :href="baseUrl"
            >NotedSource Home</a
          >
        </h4>
      </div>
    </div>
    <div v-else-if="loading" class="flex justify-center my-10 pt-[200px]">
      <i class="text-3xl fas fa-circle-notch fa-spin text-blueGray-300"></i>
    </div>
    <div v-else-if="formattedSlug" class="container h-full w-full min-w-full">
      <div
        id="metaTitle"
        class="hidden"
        v-html="`Work with experts in ${formattedSlug}`"
      ></div>
      <div
        id="metaDescription"
        class="hidden"
        v-html="expertResult?.metadata?.metaDescription"
      ></div>
      <div
        id="metaCanonical"
        class="hidden"
        v-html="`${cvBaseUrl}/expert-areas/${$route.params.slug}`"
      ></div>
      <div class="min-h-screen bg-fixed">
        <div class="w-full max-w-[1200px] mx-auto flex flex-wrap">
          <div v-if="expertResult?.results" class="w-full lg:w-3/4 pb-10">
            <div class="pt-[50px] px-4 md:px-8 mx-auto relative z-3">
              <div>
                <h1 class="text-2xl font-bold font-headline text-blueDianne">
                  Work with thought leaders and academic experts in
                  {{ expertResult?.metadata?.researchInterest }}
                </h1>
              </div>
              <div>
                <p
                  class="text-lg text-black font-light whitespace-pre-wrap mb-3 mt-2"
                >
                  {{ expertResult?.metadata?.onPageDescription }}
                </p>
                <p
                  v-if="expertResult.results"
                  class="text-sm text-black font-light"
                >
                  Researchers on NotedSource with backgrounds in
                  {{ expertResult?.metadata?.researchInterest }} include
                  <span
                    v-for="(nameWithLink, index) in expertNamesWithLinks"
                    :key="index"
                    ><router-link
                      v-if="nameWithLink.slug"
                      class="underline"
                      :to="`/me/${nameWithLink.slug}`"
                      >{{ nameWithLink.name }}</router-link
                    ><router-link
                      v-else-if="nameWithLink.hashedId"
                      class="underline"
                      :to="`/me/${nameWithLink.hashedId}`"
                      >{{ nameWithLink.name }}</router-link
                    ><span v-if="index < expertNamesWithLinks.length - 1"
                      >,
                      <span v-if="index == expertNamesWithLinks.length - 2">
                        and
                      </span></span
                    ></span
                  >.
                </p>
              </div>
              <div
                v-for="profile in expertResult.results"
                :key="profile.hashedId"
                class="w-full"
              >
                <router-link v-if="profile.slug" :to="`/me/${profile.slug}`">
                  <profile-list-item
                    :profile="profile"
                    :research-interests="[formattedSlug]"
                  ></profile-list-item>
                </router-link>
                <router-link v-else :to="`/me/${profile.hashedId}`">
                  <profile-list-item
                    :profile="profile"
                    :research-interests="[formattedSlug]"
                  ></profile-list-item>
                </router-link>
              </div>
            </div>
          </div>
          <div
            v-if="expertResult?.results"
            class="w-full lg:w-1/4 md:px-5 pt-[50px] bg-tiara-200 dark:bg-blueDianne-900 pb-10"
          >
            <h2 class="text-xl font-headline text-blueDianne">
              Example {{ expertResult?.metadata?.researchInterest }} projects
            </h2>
            <p class="text-sm my-3 text-blueDianne">
              How can companies collaborate more effectively with researchers,
              experts, and thought leaders to make progress on
              {{ expertResult?.metadata?.researchInterest }}?
            </p>
            <div
              class="project-examples"
              v-html="expertResult?.metadata?.projectExamples"
            ></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { computed } from "vue";
import { useHead } from "@unhead/vue";
import { utils } from "common-frontend";
import ProfileListItem from "../components/ProfileListItem.vue";

const { keysToCamel } = utils;

async function loadData(ontoObjects, slug) {
  const resp = await axios.get(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/person/public-profile/for-research-interest/${slug}/`,
  );
  const data = keysToCamel(resp.data);
  if (Object.keys(data.results).length === 0) {
    ontoObjects.forEach((obj) => {
      obj.notFound = true;
      obj.loading = false;
    });
  } else {
    ontoObjects.forEach((obj) => {
      obj.expertResult = data;
      obj.loading = false;
    });
  }
}

export default {
  components: {
    ProfileListItem,
  },
  async beforeRouteEnter(to) {
    const toReplace = ["-and-", "-or-"];
    if (toReplace.some((element) => to.params.slug.indexOf(element) > -1)) {
      to.params.slug = to.params.slug
        .replace("-and-", "-")
        .replace("-or-", "-");
    }
    if (to.params.slug[0] === "-") {
      to.params.slug = to.params.slug.slice(0, 1);
    }
    if (to.params.slug[to.params.slug.length - 1] === "-") {
      to.params.slug = to.params.slug.slice(0, -1);
    }
    const formattedSlug = decodeURIComponent(
      to.params.slug.replaceAll("-", " "),
    );
    to.matched.forEach((toRouteMatched) => {
      toRouteMatched.props.formattedSlug = formattedSlug;
    });
    const routes = to.matched.map((route) => {
      return route.props;
    });
    await loadData(routes, to.params.slug);
  },
  data() {
    return {
      notFound: this.$route.matched[0].props.notFound,
      formattedSlug: this.$route.matched[0].props.formattedSlug,
      expertResult: this.$route.matched[0].props.expertResult,
      loading: this.$route.matched[0].props.loading,
      baseUrl: import.meta.env.VITE_BASE_URL,
      cvBaseUrl: import.meta.env.VITE_CV_BASE_URL,
    };
  },
  computed: {
    expertNameString() {
      if (this.expertResult?.results) {
        const names = this.expertResult.results.map((result) => {
          return result.fullDisplayName ? result.fullDisplayName : "";
        });
        return `${names.slice(0, -1).join(", ")}, and ${names.slice(-1)}`;
      }
      return "";
    },
    expertNamesWithLinks() {
      if (this.expertResult?.results) {
        return this.expertResult.results.map((result) => {
          return {
            name: result.fullDisplayName,
            slug: result.slug,
            hashedId: result.hashedId,
          };
        });
      }
      return [];
    },
  },
  mounted() {
    useHead({
      title: computed(
        () => `Work with thought leaders and experts in ${this.formattedSlug}`,
      ),
      meta: [
        {
          name: `description`,
          content: computed(() => this.expertResult?.metadata?.metaDescription),
        },
        {
          name: `og:description`,
          content: computed(() => this.expertResult?.metadata?.metaDescription),
        },
      ],
    });
  },
};
</script>

<style lang="scss">
.project-examples {
  h3 {
    font-size: 14pt;
    margin-top: 25px;
  }
  p {
    font-size: 10pt;
  }
}
</style>
